import React, { useEffect } from 'react';
import { Outlet } from 'react-router';
import { useAppDispatch } from 'store/store';
import { useNavigate } from 'features/cobrand/hooks/useCobrandUtils';
import { useSelector } from 'react-redux';
import { selectIsAssessmentUserVerified } from 'features/assessments/assessmentsAuthSlice';
import { useAssessmentsPaths } from 'features/assessments/hooks/useAssessmentsPaths';
import { useAssessmentsNativeBridge } from 'features/assessments/hooks/useAssessmentsNativeBridge';
import { AssessmentsNotFound } from 'components/AssessmentsNotFound/AssessmentsNotFound';
import { AssessmentsContentLoading } from 'components/AssessmentsContentLoading/AssessmentsContentLoading';
import { useOfflineContext } from 'contexts/OfflineContext/OfflineContext';

export const UnauthenticatedAssessmentsGuard: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isAssessmentsUserVerified = useSelector(selectIsAssessmentUserVerified);
  const { assessementsVerifyPage, assessmentsPage, hasValidParams } = useAssessmentsPaths();
  const { setVarient } = useOfflineContext();
  // When running inside the mobile app's webview, holds off redirecting until the native app
  // delivers auth (or times out and we fall back to the manual verify-login flow below).
  const { status: bridgeStatus } = useAssessmentsNativeBridge();
  const isWaitingForNativeAuth = bridgeStatus === 'waiting';

  useEffect(() => {
    setVarient('assessment-offline-alert');

    return () => {
      setVarient('offline-alert');
    };
  }, []);

  useEffect(() => {
    if (isWaitingForNativeAuth) {
      return;
    }

    if (!isAssessmentsUserVerified) {
      navigate(assessementsVerifyPage, { replace: true });
      return;
    }

    navigate(assessmentsPage, { replace: true });
  }, [
    isWaitingForNativeAuth,
    isAssessmentsUserVerified,
    dispatch,
    navigate,
    assessementsVerifyPage,
    assessmentsPage,
  ]);

  // Show not found screen when required URL parameters are missing or invalid
  if (!hasValidParams) {
    return <AssessmentsNotFound hideBackHomeButton />;
  }

  if (isWaitingForNativeAuth) {
    return <AssessmentsContentLoading />;
  }

  // Render child routes when all conditions are met
  return <Outlet />;
};
